import toast from "react-hot-toast";
import ApiService from "../api/ApiServices";

const REFERRAL_STORAGE_KEY = "referralCode";

export interface ReferralNotification {
  referredUserId: string;
  username: string;
}

export interface ReferralInvitedBy {
  name: string;
  referralCode: string;
  rewarded: boolean;
}

export interface ReferralInfo {
  referralCode: string;
  shareLink: string;
  shareMessage: string;
  showReferralPrompt: boolean;
  canApplyReferralCode: boolean;
  invitedBy: ReferralInvitedBy | null;
  totalReferred: number;
  rewardedReferred: number;
  totalReferralPoints: number;
  rewardPoints: { referrer: number; referredUser: number };
  notifications: {
    joined: ReferralNotification[];
    rewarded: ReferralNotification[];
  };
}

// Keep the ?ref= code from the shared sign-up link across onboarding steps
export const captureReferralFromUrl = (search: string) => {
  const code = new URLSearchParams(search).get("ref");
  if (!code) return;
  try {
    localStorage.setItem(REFERRAL_STORAGE_KEY, code.trim().toUpperCase());
  } catch (e) {}
};

export const getStoredReferralCode = (): string => {
  try {
    return localStorage.getItem(REFERRAL_STORAGE_KEY) || "";
  } catch (e) {
    return "";
  }
};

export const clearStoredReferralCode = () => {
  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch (e) {}
};

export const REFERRAL_SHARE_SHEET_EVENT = "referral-share-sheet-open";

export const copyReferralLink = async (info: ReferralInfo) => {
  try {
    await navigator.clipboard.writeText(info.shareLink);
    toast.success("Invite link copied!");
  } catch (e) {
    toast.error("Unable to copy. Your code is " + info.referralCode);
  }
};

// Opens the native share sheet, falling back to our own share sheet
// (ReferralShareSheet) on browsers without navigator.share, e.g. desktop Firefox
export const shareReferral = async (info: ReferralInfo | null) => {
  if (!info?.shareLink) {
    toast.error("Unable to load your invite link. Please try again.");
    return;
  }

  if (navigator.share) {
    try {
      await navigator.share({ text: info.shareMessage, url: info.shareLink });
      return;
    } catch (error: any) {
      // User closed the share sheet
      if (error?.name === "AbortError") return;
    }
  }

  window.dispatchEvent(new CustomEvent(REFERRAL_SHARE_SHEET_EVENT, { detail: info }));
};

// Applies a friend's code for a user who signed up without one.
// Returns the error to show under the input, or who invited her on success.
export const applyReferralCode = async (
  code: string,
  points = 100
): Promise<{ error: string | null; invitedBy?: ReferralInvitedBy }> => {
  try {
    const response = await ApiService.applyReferralCode(code);
    if (!response?.status) return { error: response?.message || "Invalid referral code" };
    const { referrerName, referralCode, rewarded } = response.data || {};
    toast.success(
      rewarded
        ? `Code added! You both got ${points} bonus pts! 🎉`
        : `Code added! Do your first challenge or check-in to get your ${points} bonus pts.`
    );
    return { error: null, invitedBy: { name: referrerName, referralCode, rewarded: Boolean(rewarded) } };
  } catch (e) {
    return { error: "Something went wrong. Please try again." };
  }
};
